import type { JsonValue } from '@seen/core/json'
import { z } from 'zod'

export const ORBIT_SIDEBAR_BANNER_GUIDE_TYPE = 'orbit-sidebar-banner'

export const OrbitSidebarBannerBadge = {
  Announcement: 'Announcement',
  Feature: 'Feature',
  Maintenance: 'Maintenance',
} as const

export const OrbitSidebarBannerBadgeSchema = z.enum([
  OrbitSidebarBannerBadge.Announcement,
  OrbitSidebarBannerBadge.Feature,
  OrbitSidebarBannerBadge.Maintenance,
])

export type OrbitSidebarBannerBadge = z.infer<
  typeof OrbitSidebarBannerBadgeSchema
>

export interface GuideButton {
  text: string
  href?: string | undefined
}

export interface GuideImage {
  url: string
  alt: string
}

export interface OrbitSidebarBannerGuideContent {
  badge?: OrbitSidebarBannerBadge | undefined
  emoji?: string | undefined
  title: string
  body?: string | undefined
  modalTitle: string
  modalBody: string
  image?: GuideImage | undefined
  primaryButton?: GuideButton | undefined
  secondaryButton?: GuideButton | undefined
  dismissible: boolean
}

const optionalStringSchema = z
  .string()
  .trim()
  .transform((value) => value || undefined)
  .optional()

const requiredStringSchema = z.string().trim().min(1)

const badgeSchema = z
  .unknown()
  .transform((value) => {
    if (typeof value !== 'string') {
      return undefined
    }

    const normalizedBadge = value.trim().toLowerCase()
    return Object.values(OrbitSidebarBannerBadge).find(
      (badge) => badge.toLowerCase() === normalizedBadge
    )
  })
  .pipe(OrbitSidebarBannerBadgeSchema.optional())

const guideButtonSchema = z
  .object({
    text: requiredStringSchema,
    action: optionalStringSchema,
  })
  .transform(({ action, text }): GuideButton => ({ text, href: action }))

const optionalGuideButtonSchema = z.unknown().transform((value) => {
  const parsedButton = guideButtonSchema.safeParse(value)
  return parsedButton.success ? parsedButton.data : undefined
})

const guideImageSchema = z
  .object({
    url: requiredStringSchema,
    alt: optionalStringSchema,
  })
  .transform(({ alt, url }): GuideImage => ({
    alt: alt ?? '',
    url,
  }))

const optionalGuideImageSchema = z.unknown().transform((value) => {
  const parsedImage = guideImageSchema.safeParse(value)
  return parsedImage.success ? parsedImage.data : undefined
})

const orbitSidebarBannerGuideContentSchema = z
  .object({
    badge: badgeSchema,
    emoji: optionalStringSchema,
    title: requiredStringSchema,
    body: optionalStringSchema,
    modal_title: optionalStringSchema,
    modal_body: requiredStringSchema,
    image: optionalGuideImageSchema,
    primary_button: optionalGuideButtonSchema,
    secondary_button: optionalGuideButtonSchema,
    dismissible: z.boolean().optional().default(true),
  })
  .transform(
    ({
      badge,
      body,
      dismissible,
      emoji,
      image,
      modal_body: modalBody,
      modal_title: modalTitle,
      primary_button: primaryButton,
      secondary_button: secondaryButton,
      title,
    }): OrbitSidebarBannerGuideContent => ({
      badge,
      body,
      dismissible,
      emoji,
      image,
      modalBody,
      modalTitle: modalTitle ?? title,
      primaryButton,
      secondaryButton,
      title,
    })
  )

export function parseOrbitSidebarBannerGuideContent(
  rawContent: JsonValue | undefined
): OrbitSidebarBannerGuideContent | undefined {
  const parsedContent =
    orbitSidebarBannerGuideContentSchema.safeParse(rawContent)
  return parsedContent.success ? parsedContent.data : undefined
}

export function openOrbitSidebarBannerLink({
  href,
  navigate,
}: {
  href: string
  navigate: (path: string) => void
}): void {
  if (typeof window === 'undefined') {
    return
  }

  const url = new URL(href, window.location.origin)

  if (url.origin === window.location.origin) {
    navigate(`${url.pathname}${url.search}${url.hash}`)
    return
  }

  window.open(url.href, '_blank', 'noopener,noreferrer')
}
